import type { FileDisplay } from "@/services/job/job.types";

export type { FileDisplay };

/** Supported attachment host entities. */
export type AttachmentsEntityKind = "job" | "asset" | "record" | "ticket";

/** Default page size for attachment list queries / route loaders. */
export const ATTACHMENTS_FILES_PAGE_SIZE = 100;

export interface AttachmentsListQueryArgs {
	organizationId: string;
	start: number;
	count: number;
}

export interface AttachmentsFileQueryArgs {
	organizationId: string;
	fileInfoId: string;
}

export interface AttachmentsUploadVars {
	organizationId: string;
	entityId: string;
	file: File | Blob;
	onUploadProgress?: (percent: number) => void;
	description?: string;
	entityAttachmentRequirementTagId?: string | null;
}

export interface AttachmentsDeleteVars {
	organizationId: string;
	entityId: string;
	fileId: string;
}

export interface AttachmentsEditVars {
	organizationId: string;
	entityId: string;
	fileId: string;
	name?: string | null;
	description?: string | null;
}

/**
 * Mutation surface the workspace panel needs (unified variable shapes).
 * Intentionally narrower than full {@link UseMutationResult}.
 */
export interface AttachmentsMutationApi<TVars> {
	mutateAsync: (vars: TVars) => Promise<unknown>;
	reset: () => void;
	isPending: boolean;
	error: Error | null;
}

/**
 * Entity-specific attachment data access.
 *
 * Built by `use*AttachmentsAdapter` hooks so underlying mutation hooks stay
 * rules-of-hooks safe. Mutation fields are pre-bound with unified variable shapes.
 */
export interface AttachmentsAdapter {
	readonly kind: AttachmentsEntityKind;
	readonly entityId: string;
	/** Returns TanStack `queryOptions(...)` for the entity file list. */
	listQueryOptions: (args: AttachmentsListQueryArgs) => unknown;
	/** Returns TanStack `queryOptions(...)` for file content. */
	contentQueryOptions: (args: AttachmentsFileQueryArgs) => unknown;
	/** Returns TanStack `queryOptions(...)` for file thumbnail. */
	thumbnailQueryOptions: (args: AttachmentsFileQueryArgs) => unknown;
	readonly uploadMutation: AttachmentsMutationApi<AttachmentsUploadVars>;
	readonly deleteMutation: AttachmentsMutationApi<AttachmentsDeleteVars>;
	readonly editMutation: AttachmentsMutationApi<AttachmentsEditVars>;
}
